/**
 * React Query hooks for QMS Ticket Attachments
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  fetchQmsAttachments,
  uploadQmsAttachment,
  deleteQmsAttachment,
} from '../api/qmsApi';
import type { QmsAttachment } from '../types/qms.types';

// =============================================================================
// Query Key Factory
// =============================================================================

export const qmsAttachmentKeys = {
  all: (ticketId: string) => ['qms-attachments', ticketId] as const,
};

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to fetch attachments for a ticket
 */
export function useQmsAttachments(ticketId: string | undefined) {
  return useQuery({
    queryKey: qmsAttachmentKeys.all(ticketId!),
    queryFn: () => fetchQmsAttachments(ticketId!),
    enabled: !!ticketId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to upload a ticket attachment.
 *
 * Inserts an optimistic placeholder so the file appears instantly; replaces it
 * with the server's record on success, rolls back on error. Callers uploading
 * a batch should invalidate the list once after Promise.allSettled rather than
 * having each mutation invalidate (which causes an N-refetch storm).
 */
export function useUploadQmsAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, file }: { ticketId: string; file: File }) =>
      uploadQmsAttachment(ticketId, file),
    onMutate: async ({ ticketId, file }) => {
      const queryKey = qmsAttachmentKeys.all(ticketId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<QmsAttachment[]>(queryKey) ?? [];
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const optimistic: QmsAttachment = {
        id: tempId,
        ticketId,
        filename: file.name,
        originalFilename: file.name,
        mimeType: file.type || undefined,
        sizeBytes: file.size,
        storagePath: '',
        uploadedById: '',
        uploadedByName: '',
        createdAt: new Date().toISOString(),
      };
      queryClient.setQueryData<QmsAttachment[]>(queryKey, (old = []) => [optimistic, ...old]);
      return { previous, tempId, queryKey };
    },
    onSuccess: (serverAttachment, _vars, context) => {
      if (!context) return;
      queryClient.setQueryData<QmsAttachment[]>(context.queryKey, (old = []) =>
        old.map((a) => (a.id === context.tempId ? serverAttachment : a)),
      );
    },
    onError: (_err, _vars, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },
  });
}

/**
 * Hook to delete a ticket attachment
 */
export function useDeleteQmsAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, attachmentId }: { ticketId: string; attachmentId: string }) =>
      deleteQmsAttachment(ticketId, attachmentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: qmsAttachmentKeys.all(variables.ticketId) });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to delete attachment';
      toast.error(message);
    },
  });
}
